/**
 * Shared merge/coercion helpers for AI-prefill draft mappers.
 *
 * Core rule (all modules): FILL-EMPTY-ONLY. AI never overwrites anything the
 * user has typed; skipped fields are reported so the user sees exactly what
 * happened. Mappers are pure functions — no React, fully unit-testable.
 */

/** True when a form value counts as "empty" for fill-empty-only merging. */
export function isEmptyValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'number') return false;
  if (typeof value === 'boolean') return false; // booleans are a user choice
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/** Case-insensitive coercion of a value onto a fixed option list. */
export function coerceEnum(value, options, aliases = {}) {
  if (!value) return null;
  const v = String(value).trim();
  const hit = options.find((o) => o.toLowerCase() === v.toLowerCase());
  if (hit) return hit;
  const alias = aliases[v.toLowerCase()];
  return alias && options.includes(alias) ? alias : null;
}

/** Coerce an ISO-ish datetime string to datetime-local format (YYYY-MM-DDTHH:MM). */
export function coerceDateTimeLocal(value) {
  if (!value) return null;
  const s = String(value).trim();
  // Already datetime-local shaped
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return s.slice(0, 16);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 16);
}

/** Coerce to a plain date string (YYYY-MM-DD). */
export function coerceDate(value) {
  if (!value) return null;
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

/** Dedupe + trim + cap a list of short phrases (hazards, controls). */
export function cleanPhraseList(items, { maxItems = 15, maxChars = 200 } = {}) {
  const out = [];
  const seen = new Set();
  for (const item of items || []) {
    const s = String(item).trim().slice(0, maxChars);
    const key = s.toLowerCase();
    if (s && !seen.has(key)) {
      seen.add(key);
      out.push(s);
    }
    if (out.length >= maxItems) break;
  }
  return out;
}

/** Attach client-generated ids to array-of-object rows (matches the form's
 *  Math.random id convention for isolation points / gas readings). */
export function withGeneratedIds(rows) {
  return (rows || []).map((r) => ({
    id: Math.random().toString(36).substr(2, 9),
    ...r,
  }));
}

/**
 * Generic fill-empty-only merge driver used by the module mappers.
 *
 * fieldSpecs: { draftKey: { label, formKey?, coerce?(value, ctx) -> value|null } }
 * formKey lets a draft key land on a differently-named form field (e.g. the
 * backend emits a `department` NAME which the form stores as `department_id`).
 * Returns { formData, applied, skipped, warnings } — never mutates inputs.
 */
export function applyDraftWithSpecs(draft, currentFormData, fieldSpecs, ctx = {}) {
  const formData = { ...currentFormData };
  const applied = [];
  const skipped = [];
  const warnings = [];

  for (const [key, rawValue] of Object.entries(draft || {})) {
    const spec = fieldSpecs[key];
    if (!spec) {
      skipped.push({ path: key, label: key, reason: 'not-allowed' });
      warnings.push(`"${key}" is not fillable on this form — ignored`);
      continue;
    }
    if (rawValue === null || rawValue === undefined ||
        (typeof rawValue === 'string' && rawValue.trim() === '') ||
        (Array.isArray(rawValue) && rawValue.length === 0)) {
      continue; // nothing to fill
    }
    let value = rawValue;
    if (spec.coerce) {
      value = spec.coerce(rawValue, ctx);
      if (value === null || value === undefined) {
        skipped.push({ path: key, label: spec.label, reason: 'invalid-value' });
        warnings.push(`${spec.label}: "${String(rawValue).slice(0, 60)}" was not valid — left blank`);
        continue;
      }
    }
    const formKey = spec.formKey || key;
    if (!isEmptyValue(currentFormData[formKey])) {
      skipped.push({ path: formKey, label: spec.label, reason: 'already-filled' });
      continue;
    }
    formData[formKey] = value;
    applied.push({ path: formKey, label: spec.label, value, draftKey: key });
  }

  return { formData, applied, skipped, warnings };
}
